import React, { useState } from "react";
import Select from "react-select";
import { getDisplayValue } from "../../../utils/displayValue";

const AreaOfInterest = ({
    selection,
    onFieldChange,
    onMajorChange,
    cities,
    universities,
    majors,
    primarySelection,
    blockedPairs = {},
    activeSelectionIndex = null,
}) => {
    const isPrimaryConfigured = Boolean(
        primarySelection?.country && primarySelection?.programme
    );

    const [isFocused, setIsFocused] = useState({
        city: false,
        university: false,
        majors: false,
    });

    const [isTouched, setIsTouched] = useState({
        city: false,
        university: false,
        majors: false,
    });

    const fieldHasValue = (fieldValue) => {
        if (Array.isArray(fieldValue)) {
            return fieldValue.length > 0;
        }
        return Boolean(fieldValue);
    };

    const handleFocus = (field) => {
        setIsFocused((prev) => ({ ...prev, [field]: true }));
    };

    const handleBlur = (field) => {
        setIsFocused((prev) => ({ ...prev, [field]: false }));
        setIsTouched((prev) => ({ ...prev, [field]: true }));
    };

    const getLabelStyles = (field, value) => {
        const hasVal = fieldHasValue(value);

        if (isFocused[field]) {
            return {
                color: "#22c55e",
                top: "-10px",
                fontSize: "12px",
                zIndex: 20,
            };
        }

        if (isTouched[field] && !hasVal) {
            return {
                color: "#ef4444",
                top: "8px",
                fontSize: "14px",
                zIndex: 20,
            };
        }

        return {
            color: "#6b7280",
            top: hasVal ? "-10px" : "8px",
            fontSize: hasVal ? "12px" : "14px",
            zIndex: 20,
        };
    };

    const getBorderColor = (field, value) => {
        if (!isPrimaryConfigured) {
            return "#d1d5db";
        }

        const hasVal = fieldHasValue(value);

        if (isFocused[field]) {
            return "#22c55e";
        }

        if (isTouched[field] && !hasVal) {
            return "#ef4444";
        }

        return "#6b7280";
    };

    const getValidationMessage = (field, value) => {
        const hasVal = fieldHasValue(value);
        const label = field.charAt(0).toUpperCase() + field.slice(1);

        return isTouched[field] && !hasVal
            ? `${label} is required`
            : `Please select ${label}`;
    };

    const commonSelectStyles = (field) => ({
        placeholder: "",
        styles: {
            control: (base, state) => ({
                ...base,
                minHeight: "42px",
                borderColor: getBorderColor(field, selection[field]),
                "&:hover": {
                    borderColor: getBorderColor(field, selection[field]),
                },
                "&:focus-within": {
                    borderColor: "#22c55e",
                    boxShadow: "0 0 0 #22c55e",
                },
                backgroundColor: state.isDisabled ? "white" : base.backgroundColor,
                zIndex: 10,
            }),
            menu: (base) => ({
                ...base,
                zIndex: 30,
            }),
            option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                    ? "white"
                    : state.isFocused
                    ? "#3265CB"
                    : base.backgroundColor,
                color: state.isSelected
                    ? "#374151"
                    : state.isFocused
                    ? "white"
                    : base.color,
                "&:active": {
                    backgroundColor: "#3265CB",
                    color: "white",
                },
            }),
            indicatorSeparator: () => ({
                display: "none",
            }),
        },
    });

    const normalizeKey = (city, university) =>
        `${getDisplayValue(city, '').toLowerCase()}::${getDisplayValue(university, '').toLowerCase()}`;

    const isCombinationBlocked = (city, university) => {
        if (!city || !university) {
            return false;
        }

        const key = normalizeKey(city, university);
        const blockedIndices = blockedPairs[key];

        if (!Array.isArray(blockedIndices) || blockedIndices.length === 0) {
            return false;
        }

        if (activeSelectionIndex === null) {
            return true;
        }

        return blockedIndices.some((idx) => idx !== activeSelectionIndex);
    };

    const availableUniversities = () => {
        if (!selection.city) {
            return universities;
        }

        return universities.filter(
            (university) => !isCombinationBlocked(selection.city, university)
        );
    };

    const filteredUniversityOptions = availableUniversities();

    const resolvedUniversityOptions = React.useMemo(() => {
        const optionSet = new Map(
            filteredUniversityOptions.map((university) => [
                getDisplayValue(university, ''),
                true,
            ])
        );

        if (
            selection.university &&
            !optionSet.has(getDisplayValue(selection.university, ''))
        ) {
            return [selection.university, ...filteredUniversityOptions];
        }

        return filteredUniversityOptions;
    }, [filteredUniversityOptions, selection.university]);

    return (
        <div>
            <h2 className="text-gray-800 text-lg font-semibold mt-6 mb-4">
                University Preferences *
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <div className="relative">
                    <span
                        style={getLabelStyles("city", selection.city)}
                        className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
                    >
                        City *
                    </span>
                    <Select
                        id="city"
                        name="city"
                        options={cities.map((city) => ({
                            value: getDisplayValue(city, ""),
                            label: getDisplayValue(city, ""),
                        }))}
                        value={
                            selection.city
                                ? {
                                      value: getDisplayValue(selection.city, ""),
                                      label: getDisplayValue(selection.city, ""),
                                  }
                                : null
                        }
                        onChange={(selected) => {
                            onFieldChange("city", selected ? selected.value : "");
                        }}
                        onFocus={() => handleFocus("city")}
                        onBlur={() => handleBlur("city")}
                        isDisabled={!isPrimaryConfigured}
                        {...commonSelectStyles("city")}
                    />
                    <span
                        className="text-xs mt-1"
                        style={{
                            color:
                                isTouched.city && !selection.city ? "#ef4444" : "#6b7280",
                        }}
                    >
                        {getValidationMessage("city", selection.city)}
                    </span>
                </div>

                <div className="relative">
                    <span
                        style={getLabelStyles("university", selection.university)}
                        className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
                    >
                        University *
                    </span>
                    <Select
                        id="university"
                        name="university"
                        options={resolvedUniversityOptions.map((university) => ({
                            value: getDisplayValue(university, ""),
                            label: getDisplayValue(university, ""),
                        }))}
                        value={
                            selection.university
                                ? {
                                      value: getDisplayValue(selection.university, ""),
                                      label: getDisplayValue(selection.university, ""),
                                  }
                                : null
                        }
                        onChange={(selected) => {
                            onFieldChange("university", selected ? selected.value : "");
                        }}
                        onFocus={() => handleFocus("university")}
                        onBlur={() => handleBlur("university")}
                        isDisabled={!isPrimaryConfigured || !selection.city}
                        {...commonSelectStyles("university")}
                    />
                    <span
                        className="text-xs mt-1"
                        style={{
                            color:
                                isTouched.university && !selection.university
                                    ? "#ef4444"
                                    : "#6b7280",
                        }}
                    >
                        {getValidationMessage("university", selection.university)}
                    </span>
                </div>

                <div className="relative">
                    <span
                        style={getLabelStyles("majors", selection.majors)}
                        className="absolute text-sm transform left-3 bg-white px-1 transition-all duration-200"
                    >
                        Majors *
                    </span>
                    <Select
                        id="majors"
                        name="majors"
                        isMulti
                        options={majors.map((major) => ({
                            value: getDisplayValue(major, ""),
                            label: getDisplayValue(major, ""),
                        }))}
                        value={
                            Array.isArray(selection.majors)
                                ? selection.majors.map((m) => ({
                                      value: getDisplayValue(m, ""),
                                      label: getDisplayValue(m, ""),
                                  }))
                                : []
                        }
                        onChange={(selected) => {
                            const selectedValues = selected
                                ? selected.map((option) => option.value)
                                : [];
                            onMajorChange(selectedValues);
                        }}
                        onFocus={() => handleFocus("majors")}
                        onBlur={() => handleBlur("majors")}
                        isDisabled={!isPrimaryConfigured || !selection.university}
                        {...commonSelectStyles("majors")}
                    />
                    <span
                        className="text-xs mt-1"
                        style={{
                            color:
                                isTouched.majors && (!selection.majors || selection.majors.length === 0)
                                    ? "#ef4444"
                                    : "#6b7280",
                        }}
                    >
                        {getValidationMessage("majors", selection.majors)}
                    </span>
                </div>
            </div>
        </div>
    );
};

export default AreaOfInterest;
